import { useEffect, useRef, useState } from "react";

/**
 * Persist form state to localStorage as a draft so users don't lose
 * data if the tab reloads, they switch apps for the camera, or the
 * browser kills the process during long sessions (>1h).
 *
 * Uses a lazy useState initializer so the draft is loaded synchronously
 * on render #0 — this avoids a race where a "save on change" effect
 * would overwrite the stored draft with the empty initial state.
 *
 * Only active when `enabled` is true (typically NOT in edit mode).
 * Returns [state, setState, { clearDraft, restored }].
 */
export function useFormDraft(key, initialValue, enabled = true, options = {}) {
  const { ignoreKeys = [] } = options;
  const restoredRef = useRef(false);

  const [state, setState] = useState(() => {
    if (!enabled) return initialValue;
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          // "Meaningful content" = any non-ignored field with a value
          // that DIFFERS from the initialValue (i.e., user actually
          // interacted). This controls whether we announce a restored
          // draft; we still restore all persisted fields either way.
          const hasContent = Object.keys(parsed).some((k) => {
            if (ignoreKeys.includes(k)) return false;
            const v = parsed[k];
            if (v === null || v === undefined || v === "") return false;
            return v !== initialValue[k];
          });
          if (hasContent) {
            restoredRef.current = true;
            return { ...initialValue, ...parsed };
          }
          // No meaningful content — treat as no draft, use initialValue.
        }
      }
    } catch (_) {
      /* ignore */
    }
    return initialValue;
  });

  const [restored, setRestored] = useState(() => restoredRef.current);

  // Save on every change.
  useEffect(() => {
    if (!enabled) return;
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch (_) {
      /* quota exceeded, ignore */
    }
  }, [key, state, enabled]);

  const clearDraft = () => {
    try {
      localStorage.removeItem(key);
    } catch (_) {
      /* ignore */
    }
    setRestored(false);
  };

  return [state, setState, { clearDraft, restored }];
}

/**
 * Persist a photo list (with server paths only) to localStorage so
 * already-uploaded photos survive a reload. In-progress uploads are
 * dropped since blob previews cannot be restored.
 */
export function usePhotoDraft(key, enabled = true) {
  const [photos, setPhotos] = useState(() => {
    if (!enabled) return [];
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (_) {
      /* ignore */
    }
    return [];
  });

  useEffect(() => {
    if (!enabled) return;
    try {
      // Only persist photos that finished uploading (have a server path).
      const persistable = photos
        .filter((p) => p.path && !p.uploading)
        .map((p) => ({ localId: p.localId, path: p.path, preview: p.preview }));
      localStorage.setItem(key, JSON.stringify(persistable));
    } catch (_) {
      /* ignore */
    }
  }, [key, photos, enabled]);

  const clearDraft = () => {
    try {
      localStorage.removeItem(key);
    } catch (_) {
      /* ignore */
    }
  };

  return [photos, setPhotos, { clearDraft }];
}
